import { Badge, Box, Heading, Stack, Text, VStack } from "@chakra-ui/layout";
import { Image } from "@chakra-ui/react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useForm } from "react-hook-form";
import { ChangeEvent, useEffect, useState } from "react";

import { RegistrationBody } from "doxecure-commons/doxecureClientTypes";
import { corporationIdentifiersDictionary } from "doxecure-commons/doxecureLocales";
import { CorporationIdentifiersDictionary, CorporationIdentifierType, PaymentStatus } from "doxecure-commons/doxecureTypes";
import { $email, $isnumber, $keys, $length, $ok } from "foundation-ts/commons";
import { TSCountry } from "foundation-ts/tscountry";
import { TSDate } from "foundation-ts/tsdate";
import { TSError } from "foundation-ts/tserrors";
import { Countries, country, Nullable, uint } from "foundation-ts/types";

import { PaymentMethodType } from "@/components/Payment/types";
import AccountStep from "@/components/Signup/AccountStep";
import ConfirmationStep from "@/components/Signup/ConfirmationStep";
import RegisterStepper from "@/components/Signup/SignupStepper";
import { Step, RegisterFormValues } from "@/components/Signup/SignupTypes";
import { SubscriptionStep } from "@/components/Signup/SubscriptionStep";
import VersionDisplay from "@components/VersionDisplay";
import { config } from "@/config/config";
import { useMollie } from "@/contexts/MollieContext";
import useAvailableCorporationIdentifiers from "@/hooks/useAvailableCorporationIdentifiers";
import useCountries from "@/hooks/useCountries";
import useFetchRegisteredInformation from "@/hooks/useFetchRegisteredInformation";
import useProductList from "@/hooks/useProductList";
import useQueryParam from "@/hooks/useQueryParams";
import useRegister from "@/hooks/useRegister";
import { useSignupOrganizationInformation } from "@/hooks/useSignupCorporationInformation";
import { $g1Color, $g1IconURL } from "@/theme/G1Style";

const STEP_ORDER: Step[] = ["subscription", "account", "confirmation"];

export const SignupView = () => {
    const { data: productsList } = useProductList(true);
    const { data: countries } = useCountries();
    const { data: availableCorporationIdentifiers } = useAvailableCorporationIdentifiers(countries);
    const registeredCorporationId = useQueryParam("corporationId");
    const { data: registeredInformation, remainingSeconds, pollStatus } = useFetchRegisteredInformation(registeredCorporationId);
    const { mutateAsync: signup } = useRegister();
    const { corporationInformation, isLoadingCI, errorCI, handleSiretChange } = useSignupOrganizationInformation();
    const { mollie, ready } = useMollie();

    const [amount, setAmount] = useState<Nullable<number>>(null);
    const [partnerCode, setPartnerCode] = useState<string>("");
    const [corporationIdentifiersDict, setCorporationIdentifiersDict] = useState<CorporationIdentifiersDictionary>({});
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Nullable<PaymentMethodType>>(null);
    const [currentStep, setCurrentStep] = useState<Step>("subscription");
    const [completedSteps, setCompletedSteps] = useState<Set<Step>>(new Set());
    const [paymentStatus, setPaymentStatus] = useState<Nullable<PaymentStatus>>(null);
    const [expiredTrialPeriod, setExpiredTrialPeriod] = useState<Nullable<string>>(null);
    const [tva, setTVA] = useState<Nullable<number>>(null);

    const { clearErrors, getValues, register, setError, setValue, trigger, watch, formState: { errors } } =
        useForm<RegisterFormValues>({ mode: "onChange", reValidateMode: "onChange" });

    const firstSuscriptionProduct = productsList?.subscriptions?.first(); // Currently, only one subscription can be choosen
    watch(["lastname", "firstname", "email", "organizationName", "organizationIdentifierType", "organizationIdentifier", "city"]);    

    const validateStep = (step: Step): boolean => {
        switch (step) {
            case "subscription":
                return true;
            case "account":
                return !$ok(errors.lastname) && $length(getValues("lastname")) > 0 &&
                       !$ok(errors.firstname) && $length(getValues("firstname")) > 0 &&
                       !$ok(errors.email) && $length(getValues("email")) > 0 &&
                       !$ok(errors.organizationName) && $length(getValues("organizationName")) > 0 &&
                       !$ok(errors.organizationIdentifierType) && $length(getValues("organizationIdentifierType")) > 0 &&
                       !$ok(errors.organizationIdentifier) && $length(getValues("organizationIdentifier")) > 0 &&
                       !$ok(errors.city) && $length(getValues("city")) > 0;
            case "billing":
               return !$ok(errors.tvaIntra) && $length(getValues("tvaIntra")) > 0;
            default:
                return false;
        }
    };

    const validateCorporationIdentifier = (id: string): boolean => {
        const selectedCountry = getValues("organizationCountry") ;
        const selectedCorporationIdentifierType = getValues("organizationIdentifierType") as CorporationIdentifierType;
        const valid = corporationIdentifiersDictionary[selectedCorporationIdentifierType!]!.validate(id.trim().replace(/\s/g, ""), selectedCountry as country);
        if (!valid) {
            setTVA(null);
        } else if (selectedCorporationIdentifierType !== "SIRET") {
            setTVA(0);
        }
        return valid;
    }

    const handleNextStep = async (current: Step, next: Step): Promise<void> => {
        if (!validateStep(current)) { return; }
        setCompletedSteps(prev => new Set(prev).add(current));
        setCurrentStep(next);
    };

    const handleCorporationChange = (e: any): void => {
        setValue(e.target.name, e.target.value);
    };

    const handleCorporationIdentifierChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const value = e.target.value;
        const corporationIdentifierType = getValues('organizationIdentifierType');
        if (corporationIdentifierType === "SIRET") {
            handleSiretChange(value);
        }
    }

    const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>): void => {
        const newCountry = TSCountry.country(e.currentTarget.value)?.alpha2Code;
        if ($ok(newCountry)) {
            const newDict = availableCorporationIdentifiers?.get(newCountry!);
            if ($ok(newDict)) {
                setCorporationIdentifiersDict(newDict!);
                setValue("organizationIdentifierType", $keys(newDict).first()!);
            }
        }
        setValue("organizationCountry", e.currentTarget.value);
    };

    const createCommonRequestBody = (captchaToken: string): RegistrationBody => {
        return {
            lastName: getValues('lastname').trim(),
            firstName: getValues('firstname').trim(),
            email: $email(getValues('email'))!,
            name: getValues('organizationName').trim(),
            corporationIdentifierType: getValues('organizationIdentifierType') as CorporationIdentifierType,
            corporationIdentifier: getValues('organizationIdentifier').trim().replace(/\s/g, ''),
            address: {
                country: getValues('organizationCountry') as country,
                city: getValues('city').trim(),
                streetNumber: getValues('streetNumber')?.trim(),
                street: getValues('street')?.trim(),
                complement: getValues('streetComplement')?.trim(),
                zipCode: getValues('zipCode')?.trim()
            },
            maxUsers: 1 as uint,
            planType: productsList!.subscriptions.first()!.apid,
            captchaToken: captchaToken,
            testmode: config.app.mollie.testmode,
            partnerCode: partnerCode.toUpperCase()
        };
    }

    const handleCardSubmit = async (token: string, captchaToken?: string): Promise<void> => {
        if (!$ok(captchaToken)) { TSError.throw("Invalid captcha token"); }
        const body = createCommonRequestBody(captchaToken!);
        body.cbToken = token;
        const data = await signup(body);
        if ($ok(data.redirectURL)) {
            localStorage.setItem("userId", data.userId);
            window.location.href = data.redirectURL!;
        }
    };

    const handleMandateSubmit = async (consumerAccount: string, consumerName: string, captchaToken?: string): Promise<void> => {
        if (!$ok(captchaToken)) { TSError.throw("Invalid captcha token"); }
        const body = createCommonRequestBody(captchaToken!);
        body.sepa = {
            consumerName: consumerName,
            consumerAccount: consumerAccount
        };
        const data = await signup(body);
        if ($ok(data)) {
            setExpiredTrialPeriod(TSDate.from(data!.endTrialPeriod)!.toString("%d %B %Y"));
            setPaymentStatus("paid");
            setCompletedSteps(prev => new Set(prev).add("confirmation"));
        }
    };

    useEffect(() => {
        const country = TSCountry.country(Countries.FR)?.alpha2Code;
        if ($ok(country)) {
            const newDict = availableCorporationIdentifiers?.get(country!);
            if ($ok(newDict)) { setCorporationIdentifiersDict(newDict!); }
        }
    }, [availableCorporationIdentifiers]);

    useEffect(() => {
        if ($ok(registeredInformation)) { 
            const corporation = registeredInformation!.corporation;
            const user = registeredInformation!.user;

            setValue("lastname", user!.lastName);
            setValue("firstname", user!.firstName);
            setValue("email", user!.email);

            setValue("organizationName", corporation!.name);
            setValue("organizationIdentifier", corporation!.corporationIdentifier);
            setValue("organizationIdentifierType", corporation!.corporationIdentifierType);
            setValue("organizationCountry", corporation!.address.country);
            setValue("city", corporation!.address.city);
            setValue("zipCode", corporation?.address.zipCode);
            setValue("streetNumber", corporation?.address.streetNumber);
            setValue("street", corporation?.address.street);
            setValue("streetComplement", corporation?.address.complement);
            setValue("tvaIntra", corporation!.vatNumber!);
            setPartnerCode(corporation!.partnerCode ?? "");
            setExpiredTrialPeriod(corporation!.trialPeriod?.end?.toString("%d %B %Y"));

            setAmount(corporation.initialFormData?.priceHT);
            setTVA(corporation.initialFormData?.vatNumber);
            setPaymentStatus(corporation.initialFormData?.paymentStatus);
            setCompletedSteps(prev => new Set(prev).add("subscription").add("account").add("billing"));
            setCurrentStep("confirmation");
        }
    }, [registeredInformation]);

    useEffect(() => {
        if ($ok(corporationInformation)) {
            setValue("organizationName", corporationInformation?.name ?? "");
            setValue("streetNumber", corporationInformation?.address?.streetNumber);
            setValue("street", corporationInformation?.address?.street);
            setValue("streetComplement", corporationInformation?.address?.complement);
            setValue("zipCode", corporationInformation?.address?.zipCode);
            setValue("city", corporationInformation?.address?.city ?? "");
            setTVA(corporationInformation?.tva);
            clearErrors('organizationIdentifier');
        } else if ($ok(errorCI)) {
            setValue("organizationName", "");
            setValue("streetNumber", "");
            setValue("street", "");
            setValue("streetComplement", "");
            setValue("zipCode", "");
            setValue("city", "");
            setTVA(null);
            setError('organizationIdentifier', { type: 'manual', message: errorCI! });
        }
    }, [corporationInformation, errorCI])

    const steps = [
        {
            key: "subscription" as Step,
            label: "Choix de votre abonnement",
            element: (
                <SubscriptionStep
                    subscriptionProduct={firstSuscriptionProduct}
                    packProducts={productsList?.packs}
                    partnerCode={partnerCode}
                    onPartnerCodeChange={setPartnerCode}
                />
            ),
            footer: (
                <Badge top={4} right={4} colorScheme="blue" variant="outline" borderRadius="full"
                       px={3} py={1} fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                    <Text fontSize="lg" color="gray.200">Total : {amount ?? firstSuscriptionProduct?.price}€ HT/an</Text>
                </Badge>
            ),
        },
        {
            key: "account" as Step,
            label: "Informations du compte",
            element: (
                <AccountStep
                    data={{
                        lastname: getValues("lastname"),
                        firstname: getValues("firstname"),
                        email: getValues("email"),
                        organizationName: getValues("organizationName"),
                        organizationIdentifier: getValues("organizationIdentifier"),
                        city: getValues("city")
                    }}
                    corporationIdentifiersDict={corporationIdentifiersDict}
                    countries={countries}
                    isSearchInProgress={isLoadingCI}
                    errors={errors}
                    register={register}
                    trigger={trigger}
                    onCorporationChange={handleCorporationChange}
                    onCorporationIdentifierChange={handleCorporationIdentifierChange}
                    onCountryChange={handleCountryChange}
                    validateCorporationIdentifier={validateCorporationIdentifier}
                />
            ),
            footer: $isnumber(tva) ? (
                <Badge top={4} right={4} colorScheme="blue" variant="outline" borderRadius="full"
                       px={3} py={1} fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                    <Text fontSize={{ base: "xx-small", md: "lg" }} color="gray.200">Taux de TVA appliqué : {tva}%</Text>
                </Badge>
            ) : null
        },
        {
            key: "confirmation" as Step,
            label: paymentStatus === "paid" ? "Confirmation" : "Choix du moyen de paiement",
            element: (
                <ConfirmationStep
                    paymentStatus={paymentStatus}
                    selectedPaymentMethod={selectedPaymentMethod}
                    companyName={config.app.title}
                    testMode={config.app.mollie.testmode}
                    expiredTrialPeriod={expiredTrialPeriod}
                    mollie={mollie}
                    mollieReady={ready}
                    onMethodChange={setSelectedPaymentMethod}
                    onCardSubmit={handleCardSubmit}
                    onMandateSubmit={handleMandateSubmit}
                />
            ),
        },
    ];

    return (
        <GoogleReCaptchaProvider reCaptchaKey={config.app.recaptcha}>
            <Box py="5%">
                <Box
                    color={$g1Color("connection.logo.write")}
                    bg={$g1Color("connection.logo.bg")}
                    paddingTop="8" paddingBottom="2" px="8"
                    maxW="3xl" mx="auto"
                    rounded={{ base: "0", md: "2xl" }}
                >
                    <VStack spacing="4" align="stretch">
                        <Stack direction="row" alignItems="center" spacing="2">
                            <Image src={$g1IconURL("connection.logo.iconurl")} alt="Application logo" boxSize="72px" />
                            <Heading as="h1" textAlign="center" size="lg" fontWeight="bold">
                                {config.app.title}
                            </Heading>
                        </Stack>

                        { !$ok(productsList) ? 
                            <Box mt={5} p={4} bg="red.600" borderColor="red.300" borderRadius="md">
                                <Text fontSize="md" color="gray.100" lineHeight="1.7">Le service d'inscription est actuellement indisponible. Veuillez réessayer dans quelques instants.</Text>
                            </Box>
                            : (
                                <>
                                { pollStatus === "fetching" && (remainingSeconds > 0) && <Text>Vérification du paiement en cours... ({remainingSeconds}s)</Text> }
                                { pollStatus === "timeout" && 
                                    <Box mt={5} p={4} bg="red.600" borderColor="red.300" borderRadius="md">
                                        <Text fontSize="md" color="gray.100" lineHeight="1.7">La vérification du paiement a expiré, veuillez contacter le support.</Text>
                                    </Box>
                                }
                                { (pollStatus === "not_started" || pollStatus === "resolved") &&
                                    <RegisterStepper
                                        steps={steps}
                                        stepOrder={STEP_ORDER}
                                        currentStep={currentStep}
                                        completedSteps={completedSteps}
                                        isSubscriptionSucceeded={pollStatus === "resolved" || paymentStatus === "paid"}
                                        summaryProps={{
                                            subscriptionName: productsList?.subscriptions.first()?.label,
                                            amount: amount ?? firstSuscriptionProduct?.price,
                                            partnerCode,
                                            tva,
                                            lastname:                   getValues("lastname")?.toUpperCase(),
                                            firstname:                  getValues("firstname")?.capitalize(),
                                            email:                      getValues("email")?.toLowerCase(),
                                            organizationName:           getValues("organizationName")?.toUpperCase(),
                                            organizationIdentifier:     getValues("organizationIdentifier")?.toUpperCase(),
                                            organizationIdentifierType: getValues("organizationIdentifierType"),
                                            address: {
                                                city:                   getValues("city")?.toUpperCase(),
                                                streetNumber:           getValues("streetNumber"),
                                                street:                 getValues("street"),
                                                streetComplement:       getValues("streetComplement"),
                                                zipCode:                getValues("zipCode")
                                            },
                                            tvaIntra:                   getValues("tvaIntra")?.toUpperCase()
                                        }}
                                        validateStep={validateStep}
                                        onStepClick={setCurrentStep}
                                        onNextStep={handleNextStep}
                                    />
                                }
                                </>
                            )
                        }
                        <VersionDisplay />
                    </VStack>
                </Box>
            </Box>
        </GoogleReCaptchaProvider>
    );
}